import { ROOT, type WizardFieldErrors } from "@/base";
import {
  showWhenMet,
  type FieldSection,
  type ManifestField,
  type ProductManifest,
} from "@/lib/brokerProducts";

// The WC wizard's Questions and coverage step (BRK-S304), as pure rules. The
// questions, their ACORD 130 wording and their order are the manifest's
// (PMS-S703's `section`, `helpText` and array order), so the portal holds no
// question text. Coverage values are asked, never silently defaulted: a
// carrier priced on a mod of 1.0 and $1M limits the broker never gave, so a
// manifest default is only ever a hint there. General information's ACORD
// questions are the exception (PMS-S812, BRK-S392): PMS reads an unanswered
// one as its default, so they start at it, shown chosen, and the broker
// confirms them all.

export const GENERAL_INFORMATION = "wc_general_information";
export const COVERAGE = "wc_coverage";
/** Schedule rating (PMS-S812): the step's third sub-step (BRK-S392). */
export const SCHEDULE_RATING = "wc_schedule_rating";
/** Basic information's manifest fields (PMS-S808), asked on the Business
 *  step's first sub-step beside the insured (BRK-S388). */
export const BASIC_INFORMATION = "wc_basic_information";
/** Characteristics & History's fields (PMS-S809), asked on the Business
 *  step's second sub-step (BRK-S389). */
export const HISTORY = "wc_history";
/** A section of the step's questions: each is its own sub-step (BRK-S387). */
export type QuestionSection =
  | typeof GENERAL_INFORMATION
  | typeof COVERAGE
  | typeof SCHEDULE_RATING;
/** The broker's confirmation that General information's answers are true
 *  and correct (PMS-S812): a Yes, never a No. A changed answer clears it. */
export const CONFIRMATION = "acord130Confirmed";
export const CONFIRMATION_REQUIRED =
  "Confirm these answers are true and correct.";
/** ACORD 130 question 6, and the % that qualifies its Yes (PMS-S703). */
export const SUBCONTRACTORS_USED = "wgiSubcontractorsUsed";
export const SUBCONTRACTED_PERCENT = "wgiSubcontractedPercent";

export const QUESTIONS_UNAVAILABLE =
  "The questions aren’t available yet: this PMS doesn’t say which step asks them.";

/** An answer as the step holds it: undefined is unanswered, never a default. */
export type WcAnswer = boolean | number | string | undefined;
export type WcAnswers = Record<string, WcAnswer>;

/** An answer as a Yes/No's value: null while unanswered. */
export const asBoolean = (v: WcAnswer) => (typeof v === "boolean" ? v : null);
/** An answer as a number control's value: undefined while unanswered. */
export const asNumber = (v: WcAnswer) =>
  typeof v === "number" ? v : undefined;

/** The step's fields by section, in the manifest's order; null when the
 *  manifest names no sections (a PMS before PMS-S703), or there is none. */
export function questionSections(manifest: ProductManifest | null): {
  general: ManifestField[];
  coverage: ManifestField[];
  schedule: ManifestField[];
} | null {
  const fields = manifest?.fields ?? [];
  const general = fields.filter((f) => f.section === GENERAL_INFORMATION);
  const coverage = fields.filter((f) => f.section === COVERAGE);
  const schedule = fields.filter((f) => f.section === SCHEDULE_RATING);
  return general.length > 0 || coverage.length > 0 || schedule.length > 0
    ? { general, coverage, schedule }
    : null;
}

/** The fields of one of the step's sections, in the manifest's order. */
export function sectionFields(
  manifest: ProductManifest | null,
  section: QuestionSection,
): ManifestField[] {
  return (manifest?.fields ?? []).filter((f) => f.section === section);
}

/** Basic information's manifest fields, in the manifest's order (BRK-S388). */
export function basicFields(manifest: ProductManifest | null): ManifestField[] {
  return (manifest?.fields ?? []).filter(
    (f) => f.section === BASIC_INFORMATION,
  );
}

/** Characteristics & History's fields, in the manifest's order (BRK-S389). */
export function historyFields(
  manifest: ProductManifest | null,
): ManifestField[] {
  return (manifest?.fields ?? []).filter(
    (f) => f.section === HISTORY && !RETIRED_HISTORY.has(f.key),
  );
}

/** The claims questions PMS-S909 retires: the page works the answer out from
 *  each year's claims and never asks it (BRK-S505). A PMS before it still
 *  sends them, and asking them too would show the question twice. */
const RETIRED_HISTORY: ReadonlySet<string> = new Set([
  "hadWcClaims3Years",
  "wcClaimsByYear",
]);

/** The fields asked, of one section, or of both the Questions step's. */
function asked(
  manifest: ProductManifest | null,
  section?: FieldSection,
): ManifestField[] {
  if (section === BASIC_INFORMATION) return basicFields(manifest);
  if (section === HISTORY) return historyFields(manifest);
  const sections = questionSections(manifest);
  if (!sections) return [];
  if (section === GENERAL_INFORMATION) return sections.general;
  if (section === COVERAGE) return sections.coverage;
  if (section === SCHEDULE_RATING) return sections.schedule;
  return [...sections.general, ...sections.coverage, ...sections.schedule];
}

/** The answers with each General information question PMS defaults at its
 *  default while unanswered (PMS-S812): PMS reads it so, and the broker
 *  sees it chosen. No other default is ever an answer. */
export function answersWithDefaults(
  manifest: ProductManifest | null,
  answers: WcAnswers,
): WcAnswers {
  let read = answers;
  for (const f of manifest?.fields ?? []) {
    if (
      f.section === GENERAL_INFORMATION &&
      f.type === "boolean" &&
      typeof f.defaultValue === "boolean" &&
      read[f.key] === undefined
    ) {
      read = { ...read, [f.key]: f.defaultValue };
    }
  }
  return read;
}

/** A saved value, if it is one of the field's type; anything else is
 *  unanswered, including an option the field doesn't offer. */
function ofType(field: ManifestField, value: unknown): WcAnswer {
  switch (field.type) {
    case "boolean":
      return typeof value === "boolean" ? value : undefined;
    case "number":
    case "currency":
      return typeof value === "number" &&
        Number.isFinite(value) &&
        (!field.integer || Number.isInteger(value))
        ? value
        : undefined;
    case "select":
      return typeof value === "string" &&
        (field.options ?? []).some((o) => o.value === value)
        ? value
        : undefined;
    case "priorCarrier":
    case "text":
      return typeof value === "string" && value.trim() ? value : undefined;
    default:
      return undefined;
  }
}

/** What PMS holds for every sectioned field, whichever page asks it.
 *  Nothing is filled in. */
export function answersFrom(
  manifest: ProductManifest | null,
  data: Record<string, unknown>,
): WcAnswers {
  const answers: WcAnswers = {};
  for (const f of manifest?.fields ?? []) {
    if (f.section) answers[f.key] = ofType(f, data[f.key]);
  }
  return answers;
}

/** Each follow-up, by the question whose Yes asks it. */
const FOLLOW_UPS: ReadonlyMap<string, string> = new Map([
  [SUBCONTRACTED_PERCENT, SUBCONTRACTORS_USED],
]);

/** Whether the step asks a field now: the % only once subcontractors are
 *  used, and its manifest condition, one answer or any of several
 *  (PMS-S809), matches. Read it against `shownAnswers`, so a follow-up whose
 *  own question isn't asked isn't either. */
export function isAsked(field: ManifestField, answers: WcAnswers): boolean {
  if (!showWhenMet(field.showWhen, answers)) return false;
  const question = FOLLOW_UPS.get(field.key);
  return question === undefined || answers[question] === true;
}

/** The answers with every question not asked taken out, until none changes.
 *  A follow-up whose own question is hidden is hidden too (claims counted
 *  after a No to coverage), so a write never sends an answer PMS would read
 *  as one to a question it didn't ask. */
export function shownAnswers(
  fields: readonly ManifestField[],
  answers: WcAnswers,
): WcAnswers {
  let shown = answers;
  for (let changed = true; changed; ) {
    changed = false;
    for (const f of fields) {
      if (shown[f.key] !== undefined && !isAsked(f, shown)) {
        shown = { ...shown, [f.key]: undefined };
        changed = true;
      }
    }
  }
  return shown;
}

/** How many of the questions asked now hold an answer, a No and a 0
 *  included: a folded section's summary in Preview submission (BRK-S357). */
export function answeredCount(
  manifest: ProductManifest | null,
  answers: WcAnswers,
): number {
  const shown = shownAnswers(
    manifest?.fields ?? [],
    answersWithDefaults(manifest, answers),
  );
  return asked(manifest).filter((f) => {
    const answer = shown[f.key];
    return isAsked(f, shown) && answer !== undefined && answer !== "";
  }).length;
}

/** One cell of the step's two-column grid (BRK-S345). */
export interface QuestionCell {
  field: ManifestField;
  followUp: ManifestField | null;
}

/** The fields as grid cells, in the manifest's order. A follow-up rides in
 *  its question's cell, so the grid never shows it in the other column; one
 *  whose question isn't there stands alone. */
export function questionCells(
  fields: readonly ManifestField[],
): QuestionCell[] {
  const keys = new Set(fields.map((f) => f.key));
  const rides = (f: ManifestField) => keys.has(FOLLOW_UPS.get(f.key) ?? "");
  return fields
    .filter((f) => !rides(f))
    .map((field) => ({
      field,
      followUp:
        fields.find((f) => rides(f) && FOLLOW_UPS.get(f.key) === field.key) ??
        null,
    }));
}

/** The numeric control a question gets on its step, which Review's box
 *  mirrors (BRK-S349): the % of work subcontracted is a percent, money is
 *  money, and any other number is a count. Null for a field that isn't a
 *  number. */
export function numericKind(
  field: ManifestField,
): "currency" | "percent" | "number" | null {
  if (field.key === SUBCONTRACTED_PERCENT) return "percent";
  if (field.type === "currency" || field.type === "number") return field.type;
  return null;
}

function required(field: ManifestField): string {
  switch (field.type) {
    case "boolean":
      return "Choose Yes or No.";
    case "currency":
      return "Enter an amount.";
    case "number":
      return "Enter a number.";
    case "priorCarrier":
    case "select":
      return "Choose one.";
    default:
      return "Enter an answer.";
  }
}

function outOfRange(field: ManifestField, value: number): string | null {
  const { min, max } = field;
  if (min !== undefined && max !== undefined && (value < min || value > max)) {
    return `Enter a number from ${min} to ${max}.`;
  }
  if (min !== undefined && value < min) return `Enter ${min} or more.`;
  if (max !== undefined && value > max) return `Enter ${max} or less.`;
  return null;
}

/** The checks of one section, or both of the Questions step's, keyed by field
 *  as PMS keys a details refusal; {} when it may be saved. Every asked field
 *  needs an answer, in its bounds; `copy` words a missing one in its page's
 *  own words, by key. Basic information and Characteristics & History ask
 *  only what the manifest declares there, so a manifest without them blocks
 *  nothing. */
export function validateAnswers(
  manifest: ProductManifest | null,
  answers: WcAnswers,
  section?: FieldSection,
  copy: Readonly<Record<string, string>> = {},
): WizardFieldErrors {
  if (
    section !== BASIC_INFORMATION &&
    section !== HISTORY &&
    !questionSections(manifest)
  ) {
    return { [ROOT]: QUESTIONS_UNAVAILABLE };
  }
  const read = answersWithDefaults(manifest, answers);
  const shown = shownAnswers(manifest?.fields ?? [], read);
  const errors: Record<string, string> = {};
  for (const f of asked(manifest, section)) {
    // A group's rows are its page's own to check, as the payroll rows are
    // Locations'.
    if (f.type === "group" || !isAsked(f, shown)) continue;
    const value = read[f.key];
    if (f.key === CONFIRMATION && value !== true) {
      errors[f.key] = CONFIRMATION_REQUIRED;
    } else if (value === undefined || value === "") {
      if (f.required !== false) errors[f.key] = copy[f.key] ?? required(f);
    } else if (typeof value === "number") {
      if (!Number.isFinite(value) || (f.integer && !Number.isInteger(value))) {
        errors[f.key] = f.integer ? "Enter a whole number." : "Enter a number.";
        continue;
      }
      const range = outOfRange(f, value);
      if (range) errors[f.key] = range;
    }
  }
  return errors;
}

/** Whether PMS holds every answer one section, or both, asks. */
export function answersComplete(
  manifest: ProductManifest | null,
  data: Record<string, unknown>,
  section?: FieldSection,
): boolean {
  return (
    Object.keys(validateAnswers(manifest, answersFrom(manifest, data), section))
      .length === 0
  );
}

/** The partial bag of one section, or both: shown answers and nulls clearing
 *  every hidden conditional field and the subcontractor percentage in the
 *  same write. */
export function answersBag(
  manifest: ProductManifest | null,
  answers: WcAnswers,
  section?: FieldSection,
): Record<string, unknown> {
  // General information's defaults are sent as the answers they read as.
  const read = answersWithDefaults(manifest, answers);
  const shown = shownAnswers(manifest?.fields ?? [], read);
  const bag: Record<string, unknown> = {};
  for (const f of asked(manifest, section)) {
    if (!isAsked(f, shown)) {
      if (f.showWhen || f.key === SUBCONTRACTED_PERCENT) bag[f.key] = null;
      continue;
    }
    const value = read[f.key];
    if (value !== undefined) bag[f.key] = value;
  }
  return bag;
}

/** The manifest's default as a hint ("e.g. 1,000,000"), never a value. */
export function defaultHint(field: ManifestField): string | undefined {
  const d = field.defaultValue;
  if (d === undefined || typeof d === "boolean" || Array.isArray(d)) {
    return undefined;
  }
  if (field.type === "select") {
    const label = (field.options ?? []).find((o) => o.value === d)?.label;
    return label ? `e.g. ${label}` : undefined;
  }
  return `e.g. ${typeof d === "number" ? d.toLocaleString("en-US") : d}`;
}
